import { useEffect, useState, type FormEvent } from "react";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Player = {
  id: string;
  endpoint: string;
  name: string;
  intensity: string;
  enabled: number;
  timezone: string;
  start_minute: number;
  end_minute: number;
  interval_hours: number;
  character_id: string;
  blocked_reason: string | null;
  state: { ending?: string; clues?: string[] };
};
type Character = {
  id: string;
  title: string;
  description: string;
  extension: string;
  intensities: { value: string; label: string }[];
};
type State = {
  characters: Character[];
  ready: boolean;
  outboundEnabled: boolean;
  extension: string;
  phones: { phoneId: string; label: string; endpoint: string }[];
  players: Player[];
  jobs: { player_id: string; due: number; status: string }[];
  calls: { player_id: string; started: number; outcome: string | null }[];
};
const clock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const minutes = (value: FormDataEntryValue | null) => {
  const [h, m] = String(value).split(":").map(Number);
  return h! * 60 + m!;
};
const date = (seconds: number) =>
  new Date(seconds * 1000).toLocaleString(undefined, { timeZoneName: "short" });
const timeZones: Record<string, string> = {
  "America/Chicago": "Central",
  "America/New_York": "Eastern",
  "America/Denver": "Mountain",
  "America/Phoenix": "Arizona",
  "America/Los_Angeles": "Pacific",
  "America/Anchorage": "Alaska",
  "Pacific/Honolulu": "Hawaii",
};
const displayTime = (value: number) =>
  new Date(2000, 0, 1, Math.floor(value / 60), value % 60).toLocaleTimeString(
    "en-US",
    { hour: "numeric", minute: "2-digit" },
  );
const callStatus = (status: string | null) =>
  ({
    pending: "Scheduled",
    claimed: "Ringing",
    caller_hangup: "Answered; caller hung up",
    story_complete: "Completed",
    spoken_escape: "Ended by caller",
    dtmf_escape: "Ended with *",
    opted_out: "Caller stopped future calls",
    unanswered_no_retry: "Not answered; no retry",
    originate_failed_no_retry: "Could not connect; no retry",
    missed: "Missed schedule; no catch-up call",
    ineligible: "Skipped: enrollment or calling limits changed",
    duration_limit: "Time limit reached",
    service_interrupted: "Interrupted by service restart",
    uncertain_no_retry: "Interrupted; not retried",
    setup_failed: "Call setup failed",
    preferences_changed: "Rescheduled after preferences changed",
    disabled: "Cancelled",
    outbound_disabled: "Stopped by Cache River",
    operator_stopped: "Stopped from portal",
  })[status ?? ""] ?? (status ? status.replaceAll("_", " ") : "In progress");

function Preferences({
  player,
  character,
}: {
  player?: Player;
  character?: Character | undefined;
}) {
  return (
    <div className="cr-form-grid">
      <label className="cr-field">
        <span>Preferred name (optional)</span>
        <input name="name" maxLength={80} defaultValue={player?.name ?? ""} />
      </label>
      <label className="cr-field">
        <span>Intensity</span>
        <select name="intensity" defaultValue={player?.intensity ?? "spooky"}>
          {character?.intensities.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <label className="cr-field">
        <span>Local time zone</span>
        <select
          name="timezone"
          required
          defaultValue={player?.timezone ?? "America/Chicago"}
        >
          {player?.timezone && !timeZones[player.timezone] && (
            <option value={player.timezone}>{player.timezone}</option>
          )}
          {Object.entries(timeZones).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="cr-field">
        <span>Calls may start at</span>
        <input
          name="start"
          type="time"
          required
          defaultValue={clock(player?.start_minute ?? 1080)}
        />
      </label>
      <label className="cr-field">
        <span>Calls must start before</span>
        <input
          name="end"
          type="time"
          required
          defaultValue={clock(player?.end_minute ?? 1260)}
        />
      </label>
      <label className="cr-field">
        <span>Minimum hours between calls</span>
        <input
          name="interval"
          type="number"
          min={1}
          max={8760}
          required
          defaultValue={player?.interval_hours ?? 24}
        />
      </label>
    </div>
  );
}

export function AccountExperiences({
  api,
  csrf,
  accountId,
  customer = false,
  readOnly = false,
}: {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  customer?: boolean;
  readOnly?: boolean;
}) {
  const path = `/${customer ? "customer" : "admin"}/accounts/${accountId}/experiences`;
  const [state, setState] = useState<State | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [characterId, setCharacterId] = useState("caller");
  useEffect(() => {
    const abort = new AbortController();
    setState(null);
    setError("");
    setNotice("");
    api<State>(path, { signal: abort.signal })
      .then((value) => {
        if (!abort.signal.aborted) setState(value);
      })
      .catch((cause) => {
        if (!abort.signal.aborted) setError(cause.message);
      });
    const poll = window.setInterval(() => {
      api<State>(path, { signal: abort.signal })
        .then((value) => {
          if (!abort.signal.aborted) setState(value);
        })
        .catch((cause) => {
          if (!abort.signal.aborted) setError(cause.message);
        });
    }, 15000);
    return () => {
      abort.abort();
      window.clearInterval(poll);
    };
  }, [api, path, revision]);
  async function act(action: string, args: Record<string, unknown> = {}) {
    if (busy || readOnly) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(path, {
        method: "POST",
        csrf,
        body: { ...args, action, confirmed: true },
      });
      setState(await api<State>(path));
      setNotice(
        action === "call-now"
          ? "Call requested. Watch the call status below; queued does not mean answered."
          : "Add-on settings saved.",
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function save(event: FormEvent<HTMLFormElement>, player?: Player) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    void act(player ? "preferences" : "enroll", {
      ...(player
        ? { player_id: player.id }
        : {
            phoneId: form.get("phoneId"),
            consent: form.get("consent") === "on",
            character_id: characterId,
          }),
      name: form.get("name"),
      intensity: form.get("intensity"),
      timezone: form.get("timezone"),
      start_minute: minutes(form.get("start")),
      end_minute: minutes(form.get("end")),
      interval_hours: Number(form.get("interval")),
    });
  }
  const character = state?.characters?.find((item) => item.id === characterId);
  const players =
    state?.players.filter((player) => player.character_id === characterId) ??
    [];
  const phoneLabel = (playerId: string) => {
    const player = state?.players.find((item) => item.id === playerId);
    return (
      state?.phones.find((phone) => phone.endpoint === player?.endpoint)
        ?.label ?? "Enrolled phone"
    );
  };
  return (
    <section aria-label="Character add-ons" className="cr-panel">
      <h2>Character calls</h2>
      <p>
        Choose a character, enroll your phone, and set when it may call. These
        are fictional entertainment calls. Press * or say “end game” to stop.
      </p>
      {error && (
        <p role="alert" className="cr-notice error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="cr-notice">
          {notice}
        </p>
      )}
      {!state && !error && (
        <p role="status">Loading your add-ons and assigned phones...</p>
      )}
      <button
        className="cr-button secondary"
        style={{ marginBottom: 16 }}
        disabled={busy}
        onClick={() => setRevision((value) => value + 1)}
      >
        Refresh add-ons
      </button>
      {readOnly && (
        <p>Customer preview is read-only. Exit preview to make changes.</p>
      )}
      {state && (
        <>
          {(state.characters?.length ?? 0) > 1 && (
            <label className="cr-field">
              <span>Character</span>
              <select
                value={characterId}
                onChange={(event) => setCharacterId(event.target.value)}
              >
                {state.characters.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <h3>{character?.title ?? "The Caller"}</h3>
          <p>{character?.description}</p>
          <p>
            Dial {character?.extension ?? state.extension} from your Cache River
            phone. Calls last up to five minutes.
          </p>
          <p role="status">
            {state.ready
              ? "Character service connected."
              : "Character service is reconnecting."}{" "}
            {state.outboundEnabled
              ? "Opted-in players can receive calls within their saved calling windows."
              : "Automatic calls are paused by Cache River. You can still call the character yourself."}
          </p>
          {!state.phones.length && (
            <p>
              No connected Cache River phone is assigned to this account yet.
              Add equipment and prepare its phone connection first.
            </p>
          )}
          {state.phones.some(
            (phone) =>
              !players.some((player) => player.endpoint === phone.endpoint),
          ) && (
            <form className="cr-form" onSubmit={(event) => save(event)}>
              <fieldset className="cr-form-fields" disabled={busy || readOnly}>
                <legend>Enroll a phone</legend>
                <label className="cr-field">
                  <span>Phone on this account</span>
                  <select name="phoneId" required>
                    <option value="">Choose your phone</option>
                    {state.phones
                      .filter(
                        (phone) =>
                          !players.some(
                            (player) => player.endpoint === phone.endpoint,
                          ),
                      )
                      .map((phone) => (
                        <option key={phone.phoneId} value={phone.phoneId}>
                          {phone.label}
                        </option>
                      ))}
                  </select>
                </label>
                <Preferences character={character} />
                <label className="cr-check">
                  <input name="consent" type="checkbox" required /> I control
                  this phone and consent to optional fictional character calls
                  during this window.
                </label>
                <button className="cr-button">Enroll this phone</button>
              </fieldset>
            </form>
          )}
          {players.map((player) => (
            <section key={player.id} className="cr-panel">
              <h3>
                {state.phones.find(
                  (phone) => phone.endpoint === player.endpoint,
                )?.label ?? "Enrolled phone"}
              </h3>
              <p>
                {player.enabled ? "Enrollment enabled" : "Enrollment paused"}
                {player.state.ending
                  ? `. Last ending: ${player.state.ending}`
                  : ". Story progress is saved between calls."}
              </p>
              {player.blocked_reason && <p>{player.blocked_reason}</p>}
              <p>
                Calling window: {displayTime(player.start_minute)} to{" "}
                {displayTime(player.end_minute)} (
                {timeZones[player.timezone] ?? player.timezone}). At least{" "}
                {player.interval_hours} hours between calls.
              </p>
              <details style={{ marginBlock: 16 }}>
                <summary>Change calling preferences</summary>
                <form
                  className="cr-form"
                  onSubmit={(event) => save(event, player)}
                >
                  <fieldset
                    className="cr-form-fields"
                    disabled={busy || readOnly}
                  >
                    <legend>Calling preferences</legend>
                    <Preferences player={player} character={character} />
                    <button className="cr-button">Save preferences</button>
                  </fieldset>
                </form>
              </details>
              <div
                className="cr-actions"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  marginTop: 16,
                }}
              >
                <button
                  className="cr-button secondary"
                  disabled={busy || readOnly}
                  onClick={() => {
                    if (
                      !player.enabled &&
                      !window.confirm(
                        "I control this phone and renew my consent to fictional character calls within my saved calling window.",
                      )
                    )
                      return;
                    void act(player.enabled ? "disable" : "enable", {
                      player_id: player.id,
                      ...(!player.enabled ? { consent: true } : {}),
                    });
                  }}
                >
                  {player.enabled ? "Pause enrollment" : "Enable enrollment"}
                </button>
                <button
                  className="cr-button secondary"
                  disabled={
                    busy ||
                    readOnly ||
                    !player.enabled ||
                    !state.ready ||
                    !!player.blocked_reason ||
                    !state.outboundEnabled
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        "Call this enrolled phone now? Quiet hours and frequency limits still apply.",
                      )
                    )
                      void act("call-now", { player_id: player.id });
                  }}
                >
                  Call this phone now
                </button>
                <button
                  className="cr-text-button"
                  disabled={busy || readOnly}
                  onClick={() => {
                    if (window.confirm("Reset this player's story progress?"))
                      void act("reset", { player_id: player.id });
                  }}
                >
                  Restart story
                </button>
                <button
                  className="cr-text-button"
                  disabled={busy || readOnly}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Delete this player's saved name and story memory?",
                      )
                    )
                      void act("delete-memory", { player_id: player.id });
                  }}
                >
                  Delete story memory
                </button>
                <button
                  className="cr-text-button"
                  disabled={busy || readOnly}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Remove this character enrollment and its saved memory? Scheduled calls will stop.",
                      )
                    )
                      void act("delete-player", { player_id: player.id });
                  }}
                >
                  Remove enrollment
                </button>
              </div>
            </section>
          ))}
          <h3>Upcoming calls</h3>
          {state.jobs.some((job) => job.status === "pending") ? (
            <ul>
              {state.jobs
                .filter((job) => job.status === "pending")
                .map((job, index) => (
                  <li key={index}>
                    {date(job.due)} · {phoneLabel(job.player_id)}
                  </li>
                ))}
            </ul>
          ) : (
            <p>
              No calls are queued. Scheduling requires an enabled enrollment, a
              calling window, and automatic calls enabled by Cache River.
            </p>
          )}
          {state.jobs.some(
            (job) =>
              job.status !== "pending" && !job.status.startsWith("replaced_"),
          ) && (
            <>
              <h3>Recent call requests</h3>
              <ul>
                {state.jobs
                  .filter(
                    (job) =>
                      job.status !== "pending" &&
                      !job.status.startsWith("replaced_"),
                  )
                  .slice(0, 10)
                  .map((job, index) => (
                    <li key={index}>
                      {date(job.due)} · {phoneLabel(job.player_id)}:{" "}
                      {callStatus(job.status)}
                    </li>
                  ))}
              </ul>
            </>
          )}
          {!!state.calls.length && (
            <>
              <h3>Recent calls</h3>
              <ul>
                {state.calls.map((call, index) => (
                  <li key={index}>
                    {date(call.started)} · {phoneLabel(call.player_id)}:{" "}
                    {callStatus(call.outcome)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

export function Experiences({
  api,
  csrf,
  accounts,
}: {
  api: Api;
  csrf?: string | undefined;
  accounts: { id: string; name: string }[];
}) {
  const [health, setHealth] = useState<{
    ari_connected: boolean;
    outbound_enabled: boolean;
    active_calls: number;
  } | null>(null);
  const [account, setAccount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function refresh() {
    try {
      setHealth(await api("/admin/experiences"));
      setError("");
    } catch (cause) {
      setError((cause as Error).message);
    }
  }
  useEffect(() => {
    void refresh();
  }, [api]);
  async function toggle() {
    if (!health || busy) return;
    if (
      !health.outbound_enabled &&
      !window.confirm(
        "Allow automatic calls to explicitly enrolled Cache River phones? Each player's quiet hours and frequency limits remain enforced.",
      )
    )
      return;
    setBusy(true);
    try {
      await api("/admin/experiences", {
        method: "POST",
        csrf,
        body: {
          action: health.outbound_enabled
            ? "disable-outbound"
            : "enable-outbound",
          confirmed: true,
        },
      });
      await refresh();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <section className="cr-panel">
        <h2>Character calls</h2>
        <p>
          Manage character enrollments, calling windows, and call results.
          Customers can also manage their own phones from the Add-ons tab.
        </p>
        {error && <p role="alert">{error}</p>}
        {!health && !error && (
          <p role="status">Checking character service...</p>
        )}
        {health && (
          <>
            <p role="status">
              {health.ari_connected
                ? "Character service connected"
                : "Character service disconnected"}
              . Active calls: {health.active_calls}. Automatic calls:{" "}
              {health.outbound_enabled ? "enabled" : "paused"}.
            </p>
            <button className="cr-button" disabled={busy} onClick={toggle}>
              {health.outbound_enabled
                ? "Stop all automatic character calls"
                : "Enable opted-in automatic calls"}
            </button>
          </>
        )}
        <button
          className="cr-button secondary"
          disabled={busy}
          onClick={() => void refresh()}
        >
          Check service
        </button>
        <label className="cr-field">
          <span>Customer account</span>
          <select
            value={account}
            onChange={(event) => setAccount(event.target.value)}
          >
            <option value="">Choose a customer</option>
            {accounts.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </section>
      {account && (
        <AccountExperiences
          key={account}
          api={api}
          csrf={csrf}
          accountId={account}
        />
      )}
    </div>
  );
}
